/** The review box beside the item: the form, then Previous / Skip / Complete + next and the shortcuts. */

import { Box } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { formatRelative, plural } from "@/lib/format";
import { AnnotationForm, unanswered } from "./annotation-form";
import type { Answers, QueueItem, ScoreConfig } from "./types";

export interface Draft {
  answers: Answers;
  comment: string;
  /** The corrected answer itself; `null` when none. */
  corrected: unknown;
}

export function ReviewPanel({
  item,
  scores,
  draft,
  onDraft,
  canGoBack,
  saving,
  onPrevious,
  onSkip,
  onComplete,
}: {
  item: QueueItem;
  scores: ScoreConfig[];
  draft: Draft;
  onDraft: (patch: Partial<Draft>) => void;
  canGoBack: boolean;
  saving: boolean;
  onPrevious: () => void;
  onSkip: () => void;
  onComplete: () => void;
}) {
  const left = unanswered(scores, draft.answers).length;
  return (
    <Box label="Review" className="flex flex-col lg:sticky lg:top-0">
      <div className="flex items-baseline justify-between gap-2 border-b border-line-soft px-4 py-3">
        <h2 className="text-sm font-semibold">Your review</h2>
        <span className="text-xs text-muted">
          {left ? `${plural(left, "score")} left` : "Ready to complete"}
        </span>
      </div>
      {item.status === "completed" && item.completed_by && (
        <p className="m-0 border-b border-line-soft bg-success-bg px-4 py-2 text-xs text-success">
          Completed by {item.completed_by}
          {item.completed_at && ` · ${formatRelative(item.completed_at)}`}. Completing again replaces it.
        </p>
      )}
      <div className="p-4">
        <AnnotationForm
          key={item.id}
          scores={scores}
          answers={draft.answers}
          onAnswer={(id, v) => onDraft({ answers: { ...draft.answers, [id]: v } })}
          comment={draft.comment}
          onComment={(comment) => onDraft({ comment })}
          corrected={draft.corrected}
          onCorrected={(corrected) => onDraft({ corrected })}
          agentOutput={item.output}
          expectedOutput={item.expected_output}
        />
      </div>
      <div className="flex items-center gap-2 border-t border-line-soft bg-well px-4 py-3">
        <Button onClick={onPrevious} disabled={!canGoBack} title="Previous (←)">
          Previous
        </Button>
        <Button onClick={onSkip} title="Skip (S)">
          Skip
        </Button>
        <span className="flex-1" />
        <Button
          variant="primary"
          onClick={onComplete}
          disabled={left > 0 || saving}
          title={left ? "Answer every score first" : "Complete + next (⌘/Ctrl+Enter)"}
        >
          Complete + next
        </Button>
      </div>
      <p className="m-0 border-t border-line-soft px-4 py-2 text-[11px] text-faint">
        <kbd className="font-mono">⌘/Ctrl+Enter</kbd> complete · <kbd className="font-mono">S</kbd> skip ·{" "}
        <kbd className="font-mono">←</kbd> previous
      </p>
    </Box>
  );
}
